/*
 * Shared "add to cart" behaviour for product cards (homepage + collection grids).
 * Kept in one asset rather than duplicated into each section's inline <script>,
 * so the cart-add and header-count logic only exists in one place.
 */
(function () {
  'use strict';

  // Refresh the header bag count after a successful add, so it doesn't sit stale
  // until the next full page load. The badge is always rendered (hidden at zero)
  // so there is an element to update here.
  function refreshCartCount() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var el = document.getElementById('db-cart-count');
        if (!el) return;
        el.textContent = cart.item_count;
        if (cart.item_count > 0) {
          el.removeAttribute('hidden');
        } else {
          el.setAttribute('hidden', '');
        }
      })
      .catch(function () { /* count is cosmetic — never block the add on it */ });
  }

  // Magic Sidecart performs its own cart calls — adds from the product form, and every
  // quantity change or removal inside the drawer. None of those go through the code in
  // this file, so without the hooks below the badge stays stale until a page load.
  // Rather than trying to hook Sidecart's API, watch the network: any request to a cart
  // mutation endpoint, from any script, triggers a recount. Debounced because a single
  // user action can fire several (and our own handlers still call it directly).
  var countTimer = null;
  function scheduleCountRefresh() {
    clearTimeout(countTimer);
    countTimer = setTimeout(refreshCartCount, 250);
  }
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  var nativeFetch = window.fetch;
  if (nativeFetch) {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var watch = CART_WRITE.test(url);
      var result = nativeFetch.apply(this, arguments);
      if (watch && result && result.then) {
        result.then(function (res) { if (!res || res.ok) scheduleCountRefresh(); }, function () {});
      }
      return result;
    };
  }

  var nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (CART_WRITE.test(url || '')) {
      this.addEventListener('load', function () {
        if (this.status >= 200 && this.status < 300) scheduleCountRefresh();
      });
    }
    return nativeOpen.apply(this, arguments);
  };

  // Catch anything the hooks miss — a cart changed in another tab, or a form POST that
  // navigates rather than using JS — when the page is shown again from the bfcache.
  window.addEventListener('pageshow', function (e) { if (e.persisted) refreshCartCount(); });

  // ---- Cart drawer -------------------------------------------------------
  // The drawer's markup lives in sections/db-cart-drawer.liquid. After any cart
  // mutation we re-request just that section and swap it in, so the HTML is only
  // ever authored in Liquid.

  function setDrawerOpen(open) {
    var drawer = document.getElementById('db-cd');
    var overlay = document.getElementById('db-cd-overlay');
    if (!drawer || !overlay) return;
    drawer.classList.toggle('open', open);
    overlay.classList.toggle('open', open);
    document.body.style.overflow = open ? 'hidden' : '';
  }

  window.dbOpenCartDrawer = function () { setDrawerOpen(true); };
  window.dbCloseCartDrawer = function () { setDrawerOpen(false); };

  // Re-render the drawer from the server. `keepOpen` preserves the open state
  // across the swap, since replacing the node drops the .open class with it.
  function refreshCartDrawer(keepOpen) {
    return fetch('/?sections=db-cart-drawer', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        var html = data['db-cart-drawer'];
        var current = document.getElementById('shopify-section-db-cart-drawer');
        if (!html || !current) return;
        var holder = document.createElement('div');
        holder.innerHTML = html;
        var fresh = holder.querySelector('#shopify-section-db-cart-drawer') || holder.firstElementChild;
        current.replaceWith(fresh);
        if (keepOpen) setDrawerOpen(true);
      })
      .catch(function () { /* leave the stale drawer rather than blanking it */ });
  }
  window.dbRefreshCartDrawer = refreshCartDrawer;

  // line is the 1-based cart line index; quantity 0 removes the line.
  window.dbCartChange = function (line, quantity) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Could not update cart.');
        return res.json();
      })
      .then(function () {
        refreshCartCount();
        return refreshCartDrawer(true);
      })
      .catch(function (err) { alert(err.message || 'Could not update the cart.'); });
  };

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') setDrawerOpen(false);
  });

  window.dbAddToCart = function (btn, variantId) {
    if (btn.dataset.busy === '1') return;

    // Swap the label span, not the button's textContent — the latter would delete
    // the inline icon SVG.
    var label = btn.querySelector('.db-atc-label') || btn;
    var original = label.textContent;

    btn.dataset.busy = '1';
    btn.disabled = true;
    label.textContent = 'Adding…';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    })
      .then(function (res) {
        if (!res.ok) {
          return res.json().then(function (err) {
            throw new Error(err.description || err.message || 'Could not add to cart.');
          });
        }
        return res.json();
      })
      .then(function () {
        label.textContent = 'Added ✓';
        refreshCartCount();
        // Deliberately NOT opening a drawer here. Magic Sidecart is the cart UI on
        // this theme: it watches for the /cart/add.js request above and opens itself.
        // The old theme drawer used to open here, which both double-stacked with the
        // Sidecart and — once its CSS was set to display:none — left the page scroll
        // locked by setDrawerOpen() with nothing visible to close.
        setTimeout(function () {
          label.textContent = original;
          btn.disabled = false;
          btn.dataset.busy = '0';
        }, 1600);
      })
      .catch(function (err) {
        label.textContent = original;
        btn.disabled = false;
        btn.dataset.busy = '0';
        alert(err.message || 'Something went wrong. Please try again.');
      });
  };
})();
